import {
	createContext,
	use,
	useCallback,
	useMemo,
	useState,
	type ComponentPropsWithRef,
	type ComponentType,
	type ReactNode,
} from 'react';

import { useLingui } from '@lingui/react/macro';
import { Link, type LinkProps } from '@tanstack/react-router';
import { Lock, Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { useHasAnyPermission } from '@/hooks/usePermissions';
import type { PermissionKey } from '@/lib/services/permissions.service';
import { isBooleanValue, readVersioned, writeVersioned } from '@/lib/storage/versioned-storage';
import { cn } from '@/lib/utils';

export interface AreaHue {
	h: number;
	c: number;
}

// M3 color roles over per-item --h/--c vars: container tone 90 light / 30 dark, on-container inverse.
export const INDICATOR_BG = 'bg-[oklch(0.90_var(--c)_var(--h))] dark:bg-[oklch(0.38_var(--c)_var(--h))]';
export const ON_INDICATOR = 'text-[oklch(0.34_calc(var(--c)*1.5)_var(--h))] dark:text-[oklch(0.92_var(--c)_var(--h))]';
const ICON_DUOTONE_FILL = 'fill-[oklch(1_0_0/0.35)] dark:fill-[oklch(0.98_0.02_var(--h)/0.14)]';
const HOVER_TINT =
	'group-hover:text-[oklch(0.45_calc(var(--c)*1.4)_var(--h))] dark:group-hover:text-[oklch(0.85_var(--c)_var(--h))]';
const SPRING_EASE = 'ease-[cubic-bezier(0.34,1.56,0.64,1)]';

/**
 * Topbar row height, declared once on whatever wraps a rail + chrome pair. Exported with the two
 * gradients below so the boot shell skeleton paints the same chrome, at the same dimensions, as the
 * real shell it is replaced by - the swap is then a repaint rather than a layout shift.
 */
export const SHELL_HEADER_HEIGHT = '[--header-height:theme(spacing.12)]';

export const CHROME_GRADIENT =
	'bg-[linear-gradient(345deg,var(--g0),var(--g1)_35%,var(--g2)_70%,var(--g3))] [--g0:oklch(from_var(--color-primary)_0.86_calc(c*0.34)_h)] [--g1:oklch(from_var(--color-primary)_0.90_calc(c*0.22)_calc(h+14))] [--g2:oklch(from_var(--color-primary)_0.94_calc(c*0.14)_calc(h+30))] [--g3:oklch(from_var(--color-primary)_0.965_calc(c*0.10)_calc(h+48))] dark:[--g0:oklch(from_var(--color-primary)_0.31_calc(c*0.42)_h)] dark:[--g1:oklch(from_var(--color-primary)_0.27_calc(c*0.30)_calc(h+14))] dark:[--g2:oklch(from_var(--color-primary)_0.24_calc(c*0.20)_calc(h+30))] dark:[--g3:oklch(from_var(--color-primary)_0.21_calc(c*0.14)_calc(h+48))]';

// Rail shares the chrome wash so it, the topbar, and the section panel read as one surface;
// only the content card floats above it. Vertical flow starts from the chrome's lightest stop.
export const RAIL_GRADIENT =
	'bg-[linear-gradient(180deg,var(--r0),var(--r1)_55%,var(--r2))] [--r0:oklch(from_var(--color-primary)_0.965_calc(c*0.14)_calc(h+30))] [--r1:oklch(from_var(--color-primary)_0.925_calc(c*0.2)_calc(h+14))] [--r2:oklch(from_var(--color-primary)_0.885_calc(c*0.28)_h)] dark:[--r0:oklch(from_var(--color-primary)_0.24_calc(c*0.22)_calc(h+30))] dark:[--r1:oklch(from_var(--color-primary)_0.275_calc(c*0.32)_calc(h+14))] dark:[--r2:oklch(from_var(--color-primary)_0.31_calc(c*0.42)_h)]';

// Frame classes shared with `AppShellSkeleton` for the same reason as the gradients above: the
// skeleton must occupy exactly the space the real shell will, so a change here reshapes both.
export const SHELL_RAIL_FRAME = 'w-20 shrink-0 flex-col items-stretch gap-1.5 pb-3';
export const SHELL_TOPBAR_FRAME = 'flex h-(--header-height) w-full items-center px-2 md:px-3';
export const SHELL_CONTENT_FRAME =
	'mb-2 mr-2 ml-2 md:ml-0 flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl bg-background';

interface RailShellState {
	panelOpen: boolean;
	mobileOpen: boolean;
	isMobile: boolean;
	hasPanel: boolean;
}

interface RailShellActions {
	setPanelOpen: (open: boolean) => void;
	togglePanel: () => void;
	setMobileOpen: (open: boolean) => void;
}

interface RailShellContextValue {
	state: RailShellState;
	actions: RailShellActions;
}

const RailShellContext = createContext<RailShellContextValue | null>(null);

export function useRailShell(): RailShellContextValue {
	const ctx = use(RailShellContext);
	if (!ctx) throw new Error('useRailShell must be used within <RailShell>');
	return ctx;
}

// Null outside the provider so shared page headers can render the toggle safely.
export function useOptionalRailShell(): RailShellContextValue | null {
	return use(RailShellContext);
}

const readPersistedPanelOpen = (key: string | undefined) => {
	if (!key) return true;
	return readVersioned({ key, isValid: isBooleanValue }) ?? true;
};

interface RailShellProps {
	children: ReactNode;
	persistKey?: string;
	className?: string;
	hasPanel?: boolean;
}

function RailShellRoot({ children, persistKey, className, hasPanel = false }: RailShellProps) {
	const isMobile = useIsMobile();
	const [panelOpen, setPanelOpenState] = useState(() => readPersistedPanelOpen(persistKey));
	const [mobileOpen, setMobileOpen] = useState(false);

	const setPanelOpen = useCallback(
		(open: boolean) => {
			setPanelOpenState(open);
			if (!persistKey) return;
			writeVersioned({ key: persistKey, data: open });
		},
		[persistKey],
	);

	const value = useMemo<RailShellContextValue>(
		() => ({
			state: { panelOpen, mobileOpen, isMobile, hasPanel },
			actions: {
				setPanelOpen,
				togglePanel: () => setPanelOpen(!panelOpen),
				setMobileOpen,
			},
		}),
		[panelOpen, mobileOpen, isMobile, hasPanel, setPanelOpen],
	);

	return (
		<RailShellContext.Provider value={value}>
			<div className={cn('flex h-dvh', SHELL_HEADER_HEIGHT, className)}>{children}</div>
		</RailShellContext.Provider>
	);
}

interface RailProps {
	children: ReactNode;
	logo?: ReactNode;
	className?: string;
}

// Full-height rail on an M3 surface-container tone, one step removed from panel and content.
function Rail({ children, logo, className }: RailProps) {
	const { t } = useLingui();
	return (
		<nav
			className={cn('flex overflow-y-auto scrollbar-thin', SHELL_RAIL_FRAME, RAIL_GRADIENT, className)}
			aria-label={t`Areas`}
		>
			<div className="flex h-(--header-height) shrink-0 items-center justify-center">{logo}</div>
			{children}
		</nav>
	);
}

export interface RailItemIconProps {
	className?: string;
}

interface RailItemProps extends ComponentPropsWithRef<'button'> {
	hue: AreaHue;
	icon: ComponentType<RailItemIconProps>;
	label: string;
	caption?: string;
	active?: boolean;
	onSelect?: () => void;
}

// Icon over an always-visible caption; the tonal indicator springs circle-to-pill on activation.
// Extra button props pass through so composition slots (e.g. PopoverTrigger render) can drive it.
function RailItem({
	hue,
	icon: Icon,
	label,
	caption,
	active,
	disabled,
	onSelect,
	className,
	style,
	...rest
}: RailItemProps) {
	return (
		<button
			type="button"
			{...rest}
			onClick={(event) => {
				rest.onClick?.(event);
				onSelect?.();
			}}
			disabled={disabled}
			aria-label={label}
			aria-current={active ? 'true' : undefined}
			style={{ ...style, '--h': hue.h, '--c': hue.c }}
			className={cn(
				'group relative flex w-full flex-col items-center gap-1 py-2 outline-none disabled:pointer-events-none disabled:opacity-50',
				className,
			)}
		>
			<span className="relative flex h-8 w-14 items-center justify-center group-focus-visible:rounded-full group-focus-visible:ring-2 group-focus-visible:ring-ring">
				<span
					aria-hidden
					className={cn(
						'absolute h-8 rounded-full transition-all duration-300',
						SPRING_EASE,
						INDICATOR_BG,
						active ? 'w-14 opacity-100 group-active:scale-x-90 group-active:scale-y-95' : 'w-8 scale-75 opacity-0',
					)}
				/>
				<span
					aria-hidden
					className={cn(
						'absolute inset-0 rounded-full transition-colors duration-200',
						!active && 'group-hover:bg-foreground/8',
					)}
				/>
				<Icon
					className={cn(
						'relative size-5 fill-transparent transition-all duration-300',
						SPRING_EASE,
						active
							? cn('stroke-[2.2]', ON_INDICATOR, ICON_DUOTONE_FILL)
							: cn('text-muted-foreground group-hover:scale-110', HOVER_TINT),
					)}
				/>
			</span>
			<span
				className={cn(
					'w-full px-1 text-center text-[11px] leading-tight transition-colors duration-300',
					active ? 'font-semibold text-foreground' : 'text-muted-foreground group-hover:text-foreground',
				)}
			>
				{caption ?? label}
			</span>
		</button>
	);
}

// Topbar and panel share one primary-derived wash; content paints its own background over it.
function Chrome({ children, className }: { children: ReactNode; className?: string }) {
	return <div className={cn('flex min-w-0 flex-1 flex-col', CHROME_GRADIENT, className)}>{children}</div>;
}

function Body({ children }: { children: ReactNode }) {
	return <div className="flex min-h-0 flex-1">{children}</div>;
}

interface PanelProps {
	children: ReactNode;
	hidden?: boolean;
	'data-testid'?: string;
}

// Stays mounted inside a width-transitioning wrapper so collapse/expand animates.
// The panel is header-less; the open/collapse control lives in the content header.
function Panel({ children, hidden, 'data-testid': testId }: PanelProps) {
	const { state } = useRailShell();
	const open = state.panelOpen && !hidden;
	return (
		<div
			className={cn(
				'hidden shrink-0 overflow-hidden transition-[width] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] md:block',
				open ? 'w-52 2xl:w-60' : 'w-0',
			)}
			aria-hidden={!open}
			inert={!open}
		>
			<div
				className="flex h-full w-52 2xl:w-60 flex-col overflow-y-auto border-r border-white/10 pt-2 scrollbar-thin"
				data-testid={testId}
			>
				{children}
			</div>
		</div>
	);
}

// Content floats as a rounded card on the chrome gradient.
function Content({ children, className }: { children: ReactNode; className?: string }) {
	return <main className={cn(SHELL_CONTENT_FRAME, className)}>{children}</main>;
}

// Open/collapse control for the section panel; rendered at the left of the content
// header. Returns null when the active area has no panel. Safe outside the shell.
function ContentPanelToggle({ className }: { className?: string }) {
	const ctx = useOptionalRailShell();
	if (!ctx || !ctx.state.hasPanel) return null;
	const { state, actions } = ctx;
	return (
		<Button
			variant="ghost"
			size="icon"
			className={cn('hidden size-7 shrink-0 text-muted-foreground md:inline-flex', className)}
			onClick={actions.togglePanel}
			aria-label={state.panelOpen ? 'Hide panel' : 'Show panel'}
			data-testid="content-panel-toggle"
		>
			{state.panelOpen ? <PanelLeftClose className="size-4" /> : <PanelLeftOpen className="size-4" />}
		</Button>
	);
}

function MobileTrigger({ className }: { className?: string }) {
	const { t } = useLingui();
	const { actions } = useRailShell();
	return (
		<Button
			variant="ghost"
			size="icon-sm"
			className={cn('mr-1 md:hidden', className)}
			onClick={() => actions.setMobileOpen(true)}
			aria-label={t`Open menu`}
			data-testid="shell-mobile-trigger"
		>
			<Menu className="size-4" />
		</Button>
	);
}

// Rail + panel side by side inside a left sheet; caller closes it on navigation.
function MobileSheet({ children }: { children: ReactNode }) {
	const { state, actions } = useRailShell();
	if (!state.isMobile) return null;
	return (
		<Sheet open={state.mobileOpen} onOpenChange={actions.setMobileOpen}>
			<SheetContent
				side="left"
				className="p-0 data-[side=left]:w-auto data-[side=left]:max-w-[86vw]"
				showCloseButton={false}
			>
				<div className={cn('flex h-full', SHELL_HEADER_HEIGHT)}>{children}</div>
			</SheetContent>
		</Sheet>
	);
}

export const RailShell = Object.assign(RailShellRoot, {
	Rail,
	RailItem,
	Chrome,
	Body,
	Panel,
	Content,
	ContentPanelToggle,
	MobileTrigger,
	MobileSheet,
});

type PanelLinkTo = LinkProps['to'];

interface ShellPanelGroupProps {
	label?: ReactNode;
	children: ReactNode;
	// Hides the whole group (label + items) unless the user holds at least one of these. Omit to always show.
	anyPermission?: readonly PermissionKey[];
	'data-testid'?: string;
}

export function ShellPanelGroup({ label, children, anyPermission, 'data-testid': testId }: ShellPanelGroupProps) {
	const allowed = useHasAnyPermission(anyPermission ?? []);
	if (anyPermission && anyPermission.length > 0 && !allowed) return null;
	return (
		<div className="flex flex-col gap-1 p-2 pt-1" data-testid={testId}>
			{label ? (
				<span className="px-2.5 pb-0.5 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/80">
					{label}
				</span>
			) : null}
			{children}
		</div>
	);
}

const PANEL_ITEM_BASE =
	'flex w-full items-center gap-2 rounded-xl border border-transparent px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground active:inset-shadow-[0_1px_--theme(--color-black/8%)] aria-disabled:pointer-events-none aria-disabled:opacity-50';

// Active item is a primary-tinted chip, flat in light mode. Anything that varies down the item
// (a gradient fill, a flat border over that gradient, a top gloss) reads as a seam on whichever
// edge it stops matching the fill. Dark mode keeps the gradient, where the fill is dark enough
// for a lit top edge to read as elevation. Both stops paint the fill as a background image, which
// is what keeps the base hover background-colour from replacing it.
// A row whose destination sells an add-on the tenant does not hold. It stays clickable, but recedes
// so the rows that actually work read as the live ones.
const PANEL_ITEM_LOCKED = 'text-muted-foreground/55 [&_svg]:opacity-60 hover:text-muted-foreground';

const PANEL_ITEM_ACTIVE =
	'bg-linear-to-b from-primary/14 to-primary/14 font-semibold text-foreground [&_svg]:text-primary dark:border-primary/30 dark:from-primary/30 dark:to-primary/15 dark:inset-shadow-[0_1px_--theme(--color-white/12%)] hover:bg-transparent hover:text-foreground';

export interface ShellPanelItemProps {
	to: PanelLinkTo;
	params?: LinkProps['params'];
	search?: LinkProps['search'];
	activeOptions?: LinkProps['activeOptions'];
	// Forces the active state, overriding TanStack's path/search match. Use when two items share a
	// path and only a caller-derived rule (e.g. the current tab) decides which one highlights.
	active?: boolean;
	icon?: ComponentType<RailItemIconProps>;
	leading?: ReactNode;
	children: ReactNode;
	trailing?: ReactNode;
	disabled?: boolean;
	// Dims the row and marks it with a padlock when its page is behind an add-on the tenant has not
	// bought. Navigation still works: the page it lands on is the purchase wall.
	locked?: boolean;
	// Hides the item unless the user holds at least one of these. Omit to always show.
	anyPermission?: readonly PermissionKey[];
	'data-testid'?: string;
}

export function ShellPanelItem({
	to,
	params,
	search,
	activeOptions,
	active,
	icon: Icon,
	leading,
	children,
	trailing,
	disabled,
	locked,
	anyPermission,
	'data-testid': testId,
}: ShellPanelItemProps) {
	const allowed = useHasAnyPermission(anyPermission ?? []);
	const forced = active !== undefined;
	if (anyPermission && anyPermission.length > 0 && !allowed) return null;
	return (
		<Link
			to={to}
			params={params}
			search={search}
			activeOptions={activeOptions}
			data-testid={testId}
			data-active={forced && active ? 'true' : undefined}
			aria-disabled={disabled || undefined}
			tabIndex={disabled ? -1 : undefined}
			data-locked={locked ? 'true' : undefined}
			className={cn(PANEL_ITEM_BASE, locked && PANEL_ITEM_LOCKED, forced && active && PANEL_ITEM_ACTIVE)}
			activeProps={forced ? undefined : { className: PANEL_ITEM_ACTIVE, 'data-active': 'true' }}
		>
			{Icon ? <Icon className="size-4 shrink-0" /> : leading}
			<span className="truncate">{children}</span>
			{trailing ? (
				<span className="ml-auto flex shrink-0 items-center">{trailing}</span>
			) : locked ? (
				<Lock className="ml-auto size-3 shrink-0 opacity-50" aria-hidden />
			) : null}
		</Link>
	);
}
